import { useEffect, useState } from "react"
import { Breadcrumbs, BreadcrumbsProps } from "@mui/material"
import { useLocation } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import classNames from "classnames"

import useRouter, { RouterEvent } from "@/hooks/router/useRoute"
import { Route } from "@/types/route"
import Icon from "@/components/icon/Icon"
import { createGlobalStore } from "@/stores/global"

import "./breadcrumbs.scss"

interface CustomBreadCrumbsProps extends BreadcrumbsProps {
  isAtIframe: boolean
}

export default function BreadCrumbs({ isAtIframe, ...props }: CustomBreadCrumbsProps) {
  const [breadcrumbs, setBreadcrumbs] = useState<Record<string, unknown>[]>([])
  const location = useLocation()
  const pathName = location.pathname
  const router = useRouter()
  const { t } = useTranslation()
  const trailingBreadcrumb = createGlobalStore((s) => s.trailingBreadcrumb)

  const routeChangeHandler = (url?: string, route?: Route) => {
    setBreadcrumbs(trimRoute(url || "", route).filter((item) => !item.disableBreadcrumbs))
  }

  const trimRoute = (path: string, route?: Route) => {
    return recurveTrimRoute(path, route)
  }
  // MUI Breadcrumbs에 사용할 라우트 정보를 재귀적으로 추출. 뷰와 다르게 수평적인 뎊스를 지님.
  const recurveTrimRoute = (path: string, route?: Route): Record<string, unknown>[] => {
    if (route?.path === path) {
      const { name, path, disableBreadcrumbs, disableTreeView, props, auth, deprecated } = route

      return [{ name, path, disableBreadcrumbs, disableTreeView, props, auth, deprecated }]
    } else if (route?.children) {
      const result = recurveTrimRoute(path, route.children[0])
      const {
        name,
        path: routePath,
        disableBreadcrumbs,
        disableTreeView,
        props,
        auth,
        deprecated,
      } = route

      result.unshift({
        name,
        path: routePath,
        disableBreadcrumbs,
        disableTreeView,
        props,
        auth,
        deprecated,
      })

      return result
    }

    return []
  }

  useEffect(() => {
    if (!router || !router.getRoutes().length) {
      return
    }

    router?.addEventListener(RouterEvent.routeChangeStart, routeChangeHandler)

    if (breadcrumbs.length === 0) {
      router?.callChangeEvent(pathName)
    }

    return () => {
      router.removeEventListener(RouterEvent.routeChangeStart, routeChangeHandler)
    }
  }, [router, router?.getRoutes()])

  useEffect(() => {
    if (!router || !router.getRoutes().length) {
      return
    }
    // 현재 pathName이 변경되면 무조건 브레드크럼 갱신
    router.callChangeEvent(pathName)
  }, [pathName, router])

  // global store 의 trailingBreadcrumb 가 있으면 마지막 항목으로 append.
  // (route 기반 breadcrumb 위에 client state 로 임시 항목 노출 — 예: 홈 Settings 모드)
  const renderItems = trailingBreadcrumb
    ? [...breadcrumbs, { name: trailingBreadcrumb, path: undefined, deprecated: false }]
    : breadcrumbs

  // 현재 페이지가 deprecated인지 확인 (마지막 breadcrumb 기준)
  const isCurrentPageDeprecated =
    renderItems.length > 0 && Boolean(renderItems[renderItems.length - 1]?.deprecated)

  return (
    <div className="BreadCrumbsWrapper">
      <Breadcrumbs
        {...props}
        className={classNames("BreadCrumbsCont", props.className)}
        style={isAtIframe ? { paddingLeft: 28, paddingTop: 28 } : {}}>
        {renderItems.map((breadcrumb, index) => {
          const isLast = renderItems.length - 1 === index
          const path = breadcrumb?.path as string | undefined
          const clickable = !isLast && !!path

          return (
            <div
              className={classNames("bread-crumb-item", { clickable })}
              key={index}
              color="inherit"
              onClick={() => clickable && router?.push(path as string)}>
              {t(breadcrumb.name as string)}
            </div>
          )
        })}
      </Breadcrumbs>
      {isCurrentPageDeprecated && (
        <div className="breadcrumb-deprecated-notice">
          <Icon type="alert-fill" size={16} />
          <span>{t("messages.deprecatedNotice")}</span>
        </div>
      )}
    </div>
  )
}
